import {
  DocumentNode,
  FetchPolicy,
  useMutation,
  useQuery,
} from "@apollo/client";
import { GROUP, PROGRAM } from "../constants";
import React, { useEffect, useState } from "react";
import { PlaidLinkOptions } from "react-plaid-link";
import {
  CREATE_GROUP_PLAID_ACCESS,
  CREATE_ORGANIZATION_PLAID_ACCESS,
  DELETE_GROUP_PLAID_ACCESS,
  DELETE_ORGANIZATION_PLAID_ACCESS,
} from "graphql/mutations/plaid";
import { SpinnerContainer } from "./spinner";
import { GET_ORG_BANK_ACCOUNTS } from "graphql/queries/organizations";
import {
  CREATE_GROUP_PLAID_LINK,
  CREATE_ORGANIZATION_PLAID_LINK,
} from "graphql/queries/plaid";
import { GET_GROUP_BANK_ACCOUNTS } from "graphql/queries/group";
import { useAppContext } from "hooks/use-context";

type LinkBankProps = {
  openPlaid: () => void;
  setPlaidConfig: React.Dispatch<
    React.SetStateAction<PlaidLinkOptions & { isSuccess?: boolean }>
  >;
  type: "program" | "group";
  groupId?: string;
  onSuccess?: () => void;
  externalAuthId?: string | undefined;
};

function LinkBank({
  openPlaid,
  setPlaidConfig,
  type,
  groupId,
  onSuccess,
  externalAuthId,
}: LinkBankProps) {
  const { toast } = useAppContext();

  const getPlaidAccessTokenURL = (userType: string) => {
    let query = CREATE_ORGANIZATION_PLAID_ACCESS;

    if (userType === GROUP) {
      query = CREATE_GROUP_PLAID_ACCESS;
    }
    return query;
  };

  const { data, loading } = useQuery(getPlaidLinkURL(type), {
    variables:
      type === GROUP
        ? {
            spendGroupBankLinkTokenCreateId: groupId,
            id: externalAuthId,
          }
        : { id: externalAuthId },
  });
  const [deletePlaidAccessToken] = useMutation(getDeletePlaidAccessUrl(type), {
    variables: { spendGroupBankAccessTokenDeleteId: groupId },
    refetchQueries: getRefetchQueries(type, groupId),
    fetchPolicy: "network-only",
  });

  const [
    createOrgPlaidAccess,
    { data: createData, loading: createLoading, error: createErrors },
  ] = useMutation(getPlaidAccessTokenURL(type));

  useEffect(() => {
    let config: PlaidLinkOptions & { isSuccess?: boolean } = {
      onSuccess: (public_token, metadata) => {
        let status = metadata.accounts.at(0)?.verification_status ?? "";
        createOrgPlaidAccess({
          variables: getAccessVariables(
            type,
            public_token,
            status,
            groupId,
            externalAuthId ? externalAuthId : undefined
          ),
          refetchQueries: getRefetchQueries(type, groupId),
          fetchPolicy: "network-only",
        });
      },
      onExit: (err, metadata) => {
        if (err?.error_code === "TOO_MANY_VERIFICATION_ATTEMPTS") {
          deletePlaidAccessToken();
        }

        if (err?.display_message) {
          toast?.setToast({
            message: err.display_message ?? "",
            type: "danger",
          });
        }
        if (onSuccess) {
          onSuccess();
        }
      },
      onEvent: (eventName, metadata) => {},
      token: "",
    };
    if (data && data.spendOrganizationBankLinkTokenCreate) {
      config.token = data.spendOrganizationBankLinkTokenCreate.linkToken;
      setPlaidConfig(config);
    }
    if (data && data.spendGroupBankLinkTokenCreate) {
      config.token = data.spendGroupBankLinkTokenCreate.linkToken;
      setPlaidConfig(config);
    }
    // eslint-disable-next-line
  }, [data, loading, setPlaidConfig, createOrgPlaidAccess, type]);

  return (
    <>
      {createLoading ? (
        <SpinnerContainer loading={true} />
      ) : (
        <p className={`text-base flex flex-col`}>
          <span
            className={`text-blue-600 font-bold text-base cursor-pointer`}
            onClick={openPlaid}
          >
            Link External Bank Account
          </span>
        </p>
      )}
    </>
  );
}

const getAccessVariables = (
  userType: string,
  publicToken: string,
  status: string,
  groupId?: string,
  externalAuthId?: string
) => {
  let variables;

  switch (userType) {
    case PROGRAM:
      variables = {
        publicToken: publicToken,
        status,
      };
      break;
    case GROUP:
      variables = {
        publicToken: publicToken,
        spendGroupBankAccessTokenCreateId: groupId,
        status,
      };
      break;
  }

  return variables;
};

type refetchType = {
  query: DocumentNode;
  fetchPolicy?: FetchPolicy;
  variables?: any;
};

export const getRefetchQueries = (
  userType: string,
  groupId?: string
): refetchType[] => {
  let refetch: refetchType[] = [];

  switch (userType) {
    case PROGRAM:
      refetch = [
        {
          query: GET_ORG_BANK_ACCOUNTS,
          fetchPolicy: "network-only",
        },
        {
          query: CREATE_ORGANIZATION_PLAID_LINK,
        },
      ];
      break;
    case GROUP:
      refetch = [
        {
          query: GET_GROUP_BANK_ACCOUNTS,
          variables: {
            groupId,
          },
          fetchPolicy: "network-only",
        },
        {
          query: CREATE_GROUP_PLAID_LINK,
          variables: {
            spendGroupBankLinkTokenCreateId: groupId,
          },
        },
      ];
      break;
  }

  return refetch;
};

export const getDeletePlaidAccessUrl = (userType: string) => {
  let query = DELETE_ORGANIZATION_PLAID_ACCESS;

  if (userType === GROUP) {
    query = DELETE_GROUP_PLAID_ACCESS;
  }

  return query;
};

const getPlaidLinkURL = (userType: string) => {
  let query = CREATE_ORGANIZATION_PLAID_LINK;

  if (userType === GROUP) {
    query = CREATE_GROUP_PLAID_LINK;
  }

  return query;
};

export default LinkBank;
